import React, { useState, useEffect, useCallback } from 'react';
import MasterDataModal from '../components/MasterDataModal';
import VendorAgentTab from '../components/VendorAgentTab';
import APISettingsTab from '../components/APISettingsTab';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './MasterDataPage.css';

const API_URL = '/api/master-data';

function MasterDataPage() {
  const { authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('pga'); // pga | vendor_agent | api_settings
  
  // States untuk PGA Config Tab
  const [masterData, setMasterData] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});

  const fetchDependencies = useCallback(async () => {
    try {
      const [vRes, aRes] = await Promise.all([
        authFetch('/api/vendors'),
        authFetch('/api/agents')
      ]);
      const vData = await vRes.json();
      const aData = await aRes.json();
      if (vData.status === 'success') setVendors(vData.data);
      if (aData.status === 'success') setAgents(aData.data);
    } catch (err) {
      console.error("Failed to fetch dependencies", err);
    }
  }, [authFetch]);

  const fetchMasterData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(API_URL);
      const data = await response.json();
      setMasterData(data);
    } catch (error) {
      console.error("Gagal mengambil master data:", error);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (activeTab === 'pga') {
      fetchDependencies();
      fetchMasterData();
    }
  }, [activeTab, fetchDependencies, fetchMasterData]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setCurrentRecord({ is_active: true });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (record) => {
    setIsEditing(true);
    setCurrentRecord(record);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentRecord({});
  };

  const handleSave = async () => {
    const url = isEditing ? `${API_URL}/${currentRecord.id}` : API_URL;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await authFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(currentRecord),
      });
      if (!response.ok) throw new Error('Gagal menyimpan data');
      toast.success(`Record berhasil ${isEditing ? 'diperbarui' : 'ditambahkan'}!`);
      handleCloseModal();
      fetchMasterData(); // Muat ulang data setelah menyimpan
    } catch (error) {
      console.error(error);
      toast.error('Gagal menyimpan record.');
    }
  };

  const handleDelete = async (recordId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus record ini?')) {
      try {
        const response = await authFetch(`${API_URL}/${recordId}`, { method: 'DELETE' });
        if (!response.ok) throw new Error('Gagal menghapus data');
        toast.success('Record berhasil dihapus!');
        fetchMasterData();
      } catch (error) {
        console.error(error);
        toast.error(`Gagal menghapus data: ${error.message}`);
      }
    }
  };

  const handleToggleStatus = async (record, newStatus) => {
    try {
      setMasterData(prevData =>
        prevData.map(item =>
          item.id === record.id ? { ...item, is_active: newStatus } : item
        )
      );

      const response = await authFetch(`${API_URL}/${record.id}/toggle`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: newStatus }),
      });

      if (!response.ok) {
        throw new Error('Gagal memperbarui status');
      }
    } catch (error) {
      console.error(error);
      setMasterData(prevData =>
        prevData.map(item =>
          item.id === record.id ? { ...item, is_active: !newStatus } : item
        )
      );
      toast.error('Gagal memperbarui status. Silakan coba lagi.');
    }
  };

  return (
    <div className="master-data-container">
      <div className="dashboard-header">
        <h1>Master Data Management</h1>
      </div>

      <div className="tab-navigation">
        <button 
          className={activeTab === 'pga' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('pga')}
        >
          PGA Configs
        </button>
        <button 
          className={activeTab === 'vendor_agent' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('vendor_agent')}
        >
          Vendors & Agents
        </button>
        <button 
          className={activeTab === 'api_settings' ? 'tab-btn active' : 'tab-btn'} 
          onClick={() => setActiveTab('api_settings')}
        >
          Global API Settings
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'pga' && (
          <div>
            <div className="tab-header-actions">
              <h2>PGA Configurations</h2>
              <button onClick={handleOpenAddModal} className="add-new-btn">Add New PGA</button>
            </div>
            {loading ? (
              <p>Loading data...</p>
            ) : (
              <div className="table-responsive">
                <table className="master-data-table">
                  <thead>
                    <tr>
                      <th>PGA Name</th>
                      <th>Vendor</th>
                      <th>Agent</th>
                      <th>Project</th>
                      <th>Base URL</th>
                      <th>Provider ID</th>
                      <th>Merchant ID</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {masterData.map((item) => (
                      <tr key={item.id}>
                        <td>{item.pga_name}</td>
                        <td>{item.vendor_name}</td>
                        <td>{item.agent_name}</td>
                        <td>{item.project || '-'}</td>
                        <td><a href={item.base_url} target="_blank" rel="noreferrer" className="link-cell">{item.base_url}</a></td>
                        <td>{item.provider_id || '-'}</td>
                        <td>{item.merchant_id || '-'}</td>
                        <td>
                          <label className="switch-table">
                            <input 
                              type="checkbox" 
                              checked={item.is_active} 
                              onChange={(e) => handleToggleStatus(item, e.target.checked)}
                            />
                            <span className="slider round"></span>
                          </label>
                        </td>
                        <td className="actions-cell">
                          <button onClick={() => handleOpenEditModal(item)}>Edit</button>
                          <button onClick={() => handleDelete(item.id)} className="delete-btn">Delete</button>
                        </td>
                      </tr>
                    ))}
                    {masterData.length === 0 && (
                      <tr>
                        <td colSpan="9" style={{textAlign: 'center', padding: '20px'}}>No records found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <MasterDataModal
              isOpen={isModalOpen}
              onClose={handleCloseModal}
              onSave={handleSave}
              record={currentRecord}
              setRecord={setCurrentRecord}
              isEditing={isEditing}
              vendors={vendors}
              agents={agents}
            />
          </div>
        )}

        {activeTab === 'vendor_agent' && (
          <VendorAgentTab />
        )}

        {activeTab === 'api_settings' && (
          <APISettingsTab />
        )}
      </div>
    </div>
  );
}

export default MasterDataPage;